/**
 * GroupFormModal Component
 * Modal for adding and editing groups
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Button } from '../ui/Button';
import { useNavigate } from 'react-router-dom';
import { groupsService, type GroupFormData } from '../../services/groups.service';
import { membersService } from '../../services/members.service';
import { useToast } from '../../hooks/useToast';

export interface GroupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  groupId?: number;
}

export const GroupFormModal = ({ isOpen, onClose, mode, groupId }: GroupFormModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Fetch group data in edit mode
  const { data: group } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => groupsService.getById(groupId!),
    enabled: isOpen && mode === 'edit' && !!groupId,
  });

  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
  });

  const [groupLogo, setGroupLogo] = useState<File | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);

  // Group name validation state
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const nameCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalNameRef = useRef<string | null>(null);

  // Fetch members for member picker
  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
  });

  // Populate form when group data is provided (edit mode)
  useEffect(() => {
    if (isOpen && mode === 'edit' && group) {
      setFormData({
        name: group.name,
        description: group.description || '',
      });
      originalNameRef.current = group.name;
      // Load existing members
      if (group.members) {
        setSelectedMemberIds(group.members.map((m) => m.member_id));
      }
    }
  }, [isOpen, mode, group]);

  // Load group logo as blob with JWT authentication
  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (isOpen && mode === 'edit' && group && group.has_group_logo) {
        try {
          const blobUrl = await groupsService.fetchGroupImage(group.id);
          if (blobUrl && mounted) {
            setCurrentImageUrl(blobUrl);
          }
        } catch (error) {
          console.error(`Failed to load image for group ${group.id}:`, error);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [isOpen, mode, group]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', description: '' });
      setGroupLogo(null);
      setCurrentImageUrl('');
      setSelectedMemberIds([]);
      setNameError(null);
      originalNameRef.current = null;

      if (nameCheckTimeoutRef.current) {
        clearTimeout(nameCheckTimeoutRef.current);
      }

      if (mode === 'edit' && groupId) {
        groupsService.revokeGroupImage(groupId);
      }
    }
  }, [isOpen, mode, group]);

  const navigate = useNavigate();

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: GroupFormData) => groupsService.create(data),
    onSuccess: async (result) => {
      toast.success('Group created successfully');

      if (groupLogo) {
        groupsService.clearGroupImageCache();
      }

      await queryClient.refetchQueries({ queryKey: ['groups'] });
      onClose();
      navigate(`/groups/${result.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create group');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: GroupFormData) => groupsService.update(groupId!, data),
    onSuccess: async () => {
      toast.success('Group updated successfully');

      if (groupId) {
        groupsService.revokeGroupImage(groupId);
      }

      await queryClient.refetchQueries({ queryKey: ['groups'] });
      await queryClient.refetchQueries({ queryKey: ['group', groupId] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update group');
    },
  });

  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, name: value }));
    if (nameError) {
      setNameError(null);
    }
  };

  // Handle name blur - check for duplicates
  const handleNameBlur = useCallback(async () => {
    const name = formData.name.trim();

    if (!name) {
      setNameError(null);
      return;
    }

    if (mode === 'edit' && name.toLowerCase() === originalNameRef.current?.toLowerCase()) {
      setNameError(null);
      return;
    }

    if (nameCheckTimeoutRef.current) {
      clearTimeout(nameCheckTimeoutRef.current);
    }

    setIsCheckingName(true);
    nameCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await groupsService.checkNameExists(
          name,
          mode === 'edit' ? groupId : undefined
        );
        if (result.exists) {
          setNameError('Group name already exists');
        } else {
          setNameError(null);
        }
      } catch {
        setNameError(null);
      } finally {
        setIsCheckingName(false);
      }
    }, 500);
  }, [formData.name, mode, group]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        e.target.value = '';
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('File must be JPG or PNG');
        e.target.value = '';
        return;
      }

      setGroupLogo(file);
    }
  };

  // Add member
  const handleAddMember = (memberId: string) => {
    if (memberId) {
      const id = Number(memberId);
      if (!selectedMemberIds.includes(id)) {
        setSelectedMemberIds((prev) => [...prev, id]);
      }
    }
  };

  // Remove member
  const handleRemoveMember = (memberId: number) => {
    setSelectedMemberIds((prev) => prev.filter((id) => id !== memberId));
  };

  // Filter members for member picker (exclude already selected and removed members)
  const availableMembers = members.filter(
    (m) => !m.removed_at && !selectedMemberIds.includes(m.id)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    if (nameError) {
      toast.error(nameError);
      return;
    }

    const submitData: GroupFormData = {
      ...formData,
      member_ids: selectedMemberIds,
    };

    if (groupLogo) {
      submitData.group_logo = groupLogo;
    }

    if (mode === 'edit') {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? 'Edit Group' : 'Add Group'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Group Name */}
        <Input
          label="Group Name"
          name="name"
          value={formData.name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          placeholder="e.g., Design Team"
          required
          error={nameError || undefined}
          helperText={isCheckingName ? 'Checking name...' : undefined}
        />

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-body mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of the group..."
            rows={3}
            className="block w-full rounded-md border-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-heading bg-surface"
          />
        </div>

        {/* Group Members */}
        <div>
          <label className="block text-sm font-medium text-body mb-1">
            Members
          </label>

          {/* Selected members list */}
          {selectedMemberIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedMemberIds.map((memberId) => {
                const member = members.find((m) => m.id === memberId);
                if (!member) return null;
                return (
                  <span
                    key={memberId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                  >
                    {member.first_name} {member.last_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(memberId)}
                      className="text-blue-400 hover:text-blue-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Add member dropdown */}
          <SearchableSelect
            value=""
            onChange={handleAddMember}
            options={[
              { value: '', label: 'Select a member...' },
              ...availableMembers.map((member) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
              })),
            ]}
            placeholder="Search members to add..."
          />
          <p className="text-xs text-meta mt-1">
            Add any active member to this group.
          </p>
        </div>

        {/* Group Logo */}
        <div>
          <label className="block text-sm font-medium text-body mb-2">Group Logo</label>
          <div className="flex items-center space-x-4">
            {mode === 'edit' && currentImageUrl && (
              <div className="flex-shrink-0">
                <img
                  src={currentImageUrl}
                  alt="Current"
                  className="w-16 h-16 rounded-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                id="group_logo"
                name="group_logo"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-meta file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
              />
              <p className="text-xs text-meta mt-1">
                JPG or PNG, max 5MB. Image will be cropped to 160x160px.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {mode === 'edit' ? 'Update Group' : 'Create Group'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
